import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal, Form, Button, notification } from 'antd';
import ManufacturersReportForm from '../ManufacturersReportForm';
import { useGenerateManufacturersReport } from '@/core/domain/reports/stock/Report.hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { useDefaultFormErrorNotification, useReportGenerate } from '@/hooks';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { ReportManufacturersFormValues } from '@/core/domain/reports/stock/Report.types';
import SpaceForButton from '@/components/_common/SpaceForButtons';

interface Props {
  open: boolean;
  onCancel: VoidFunction;
}

function ManufacturersReportModal({ onCancel, open }: Props) {
  const intl = useIntl();
  const [form] = Form.useForm<ReportManufacturersFormValues>();
  const mutation = useGenerateManufacturersReport();
  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();
  const report = useReportGenerate();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const { finalWord, initialWord, ...values } = await form.validateFields();

      const obj = {
        ...values,
        initialWord,
        finalWord: finalWord
          ? finalWord
          : initialWord
          ? initialWord + 'z'
          : undefined,
        format: FILE_FORMAT[format],
        name: values.name || undefined,
      };

      const result = await mutation.mutateAsync(obj);

      if (callback) {
        callback(result);
      }

      notification.success({
        message: intl.formatMessage({
          id: 'manufacturers.notification.generate.report.title',
        }),
        description: intl.formatMessage({
          id: 'manufacturers.notification.generate.report.description',
        }),
      });

      onCancel();
    } catch (err) {
      sendFormErrorNotification(err);
    }
    setFileUnloading(format);
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={680}
      title={intl.formatMessage({ id: 'manufacturers.modal.report.title' })}
      footer={
        <SpaceForButton justifyContent="flex-end">
          <Button
            type="primary"
            key="viewReport"
            block
            onClick={() =>
              handleGenerateReport('REPORT_VIEW', (result) =>
                report.openFile({
                  result,
                  fileName: 'Fabricantes.pdf',
                  fileType: 'application/pdf',
                }),
              )
            }
            loading={fileLoading.REPORT_VIEW}
          >
            <FormattedMessage id="manufacturers.modal.report.buttons.viewReport" />
          </Button>
          <Button
            type="primary"
            key="pdf"
            block
            onClick={() =>
              handleGenerateReport('PDF', (result) =>
                report.downloadFile({
                  result,
                  fileName: 'Fabricantes.pdf',
                }),
              )
            }
            loading={fileLoading.PDF}
          >
            <FormattedMessage id="manufacturers.modal.report.buttons.pdf" />
          </Button>
          <Button
            type="primary"
            key="csv"
            block
            onClick={() =>
              handleGenerateReport('CSV', (result) =>
                report.downloadFile({
                  result,
                  fileName: 'Fabricantes.csv',
                }),
              )
            }
            loading={fileLoading.CSV}
          >
            <FormattedMessage id="manufacturers.modal.report.buttons.csv" />
          </Button>
        </SpaceForButton>
      }
    >
      <ManufacturersReportForm form={form} />
    </Modal>
  );
}

export default ManufacturersReportModal;
